import React from 'react';
import { CircularProgress, Box, Typography } from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
// import CachedIcon from '@mui/icons-material/Cached';
import clsx from 'clsx';
import '../styles/AudioLoader.css';

interface AudioLoaderProps {
  isLoading: boolean;
  loadingText?: string;
  size?: 'small' | 'medium' | 'large';
  showIcon?: boolean;
  variant?: 'minimal' | 'inline' | 'overlay';
  className?: string;
}

const AudioLoader: React.FC<AudioLoaderProps> = ({
  isLoading,
  loadingText = 'Generating audio...',
  size = 'small',
  showIcon = true,
  variant = 'inline',
  className,
}) => {
  if (!isLoading) return null;

  // Size mappings
  const sizeMap = {
    small: {
      progress: 16,
      icon: 'small',
      text: 'text-xs',
    },
    medium: {
      progress: 24,
      icon: 'small',
      text: 'text-sm',
    },
    large: {
      progress: 32,
      icon: 'medium',
      text: 'text-base',
    },
  };

  const currentSize = sizeMap[size];

  // Inline version - spinner with text
  if (variant === 'inline') {
    return (
      <Box className={clsx('audio-loader-inline flex items-center gap-2', className)}>
        <CircularProgress
          size={currentSize.progress}
          color="primary"
          thickness={size === 'small' ? 4 : 3.6}
          className="audio-loader-spinner"
        />
        <Typography variant="caption" className={clsx('audio-loader-text', currentSize.text)}>
          {loadingText}
        </Typography>
      </Box>
    );
  }

  // Overlay version - spinner with text and background
  return (
    <Box
      className={clsx(
        'audio-loader-overlay flex flex-col items-center justify-center p-3 bg-white/90 rounded-md shadow-sm',
        className
      )}
    >
      <Box className="audio-loader-icon-wrapper mb-2">
        {showIcon ? (
          <VolumeUpIcon
            fontSize={size === 'small' ? 'medium' : 'large'}
            className="text-primary-300 audio-pulse"
          />
        ) : (
          <CircularProgress
            size={currentSize.progress * 1.5}
            color="primary"
            className="audio-loader-spinner"
          />
        )}
      </Box>
      <Typography
        variant={size === 'small' ? 'caption' : 'body2'}
        className="audio-loader-text text-center"
      >
        {loadingText}
      </Typography>
    </Box>
  );
};

export default AudioLoader;
